import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Button } from '../ui/button';

/** P0 single-variable Target Solver result panel.
 *
 * The wrapper carries `role="status"` + `aria-live="polite"` so screen
 * readers announce the newly-computed result. The result changes only
 * on an explicit user Solve click — never on slider drag — so the
 * announcement rate stays bounded.
 */
export const SolverResult = ({ result, solveFor, currentValue, onApply }) => {
  const { t } = useTranslation();
  if (!result) return null;

  const status = result.status;
  const isSolved = status === 'solved';
  const isAlreadyMet = status === 'already_met';
  const isImpossible = status === 'impossible' || status === 'out_of_range';

  const fmtVal = (v) => {
    if (v === null || v === undefined) return '—';
    if (solveFor === 'purchase_price') return `$${Math.round(v).toLocaleString()}`;
    if (solveFor === 'rent') return `$${Math.round(v / 12).toLocaleString()}/mo`;
    if (solveFor === 'down_payment') return `${v.toFixed(1)}%`;
    if (solveFor === 'interest_rate') return `${v.toFixed(2)}%`;
    return String(v);
  };

  if (isAlreadyMet) {
    return (
      <div
        data-testid="solver-result-already-met"
        role="status"
        aria-live="polite"
        className="mt-6 p-5 rounded-xl border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/20"
      >
        <div className="flex items-start gap-3">
          <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-emerald-800 dark:text-emerald-300">{t('scenario.alreadyMet')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (isImpossible) {
    const reasonKey = result.reason_code === 'down_payment_over_100'
      ? 'scenario.impossibleDpOver100'
      : result.reason_code === 'rate_below_bound'
        ? 'scenario.impossibleRateBelow'
        : 'scenario.targetNotReachable';
    return (
      <div
        data-testid="solver-result-impossible"
        role="status"
        aria-live="polite"
        className="mt-6 p-5 rounded-xl border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/20"
      >
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-amber-800 dark:text-amber-300">{t(reasonKey)}</p>
            <p className="text-sm text-amber-700 dark:text-amber-400 mt-1">{t('scenario.targetNotReachableHint')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!isSolved) return null;

  const delta = result.delta_absolute;
  const deltaPct = result.delta_percent;
  const deltaSign = delta >= 0 ? '+' : '';
  const deltaColor = delta >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400';
  const fmtDelta = () => {
    if (delta === null || delta === undefined) return '—';
    if (solveFor === 'rent') return `${deltaSign}$${Math.round(delta / 12).toLocaleString()}/mo`;
    if (solveFor === 'purchase_price') return `${deltaSign}$${Math.round(delta).toLocaleString()}`;
    if (solveFor === 'down_payment' || solveFor === 'interest_rate') return `${deltaSign}${delta.toFixed(2)}%`;
    return String(delta);
  };

  const requiredLabel = solveFor === 'purchase_price' ? t('scenario.maximum') : t('scenario.required');

  return (
    <div
      data-testid="solver-result-solved"
      role="status"
      aria-live="polite"
      className="mt-6 p-5 rounded-xl border border-violet-200 dark:border-violet-800 bg-white dark:bg-[#0F1419]"
    >
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
        <div>
          <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">{t('scenario.current')}</p>
          <p data-testid="solver-result-current" className="text-xl font-primary font-bold text-slate-900 dark:text-slate-100">{fmtVal(currentValue)}</p>
        </div>
        <div>
          <p className="text-xs text-violet-600 dark:text-violet-400 uppercase tracking-wider mb-1">{requiredLabel}</p>
          <p data-testid="solver-result-solved-value" className="text-xl font-primary font-bold text-violet-700 dark:text-violet-300">{fmtVal(result.solved_value)}</p>
        </div>
        <div>
          <p className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">{t('scenario.difference')}</p>
          <p data-testid="solver-result-delta" className={`text-xl font-primary font-bold ${deltaColor}`}>
            {fmtDelta()}
            {deltaPct !== null && deltaPct !== undefined && (
              <span className="text-sm ml-1 opacity-70">({deltaPct >= 0 ? '+' : ''}{deltaPct.toFixed(1)}%)</span>
            )}
          </p>
        </div>
      </div>
      <div className="pt-3 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between">
        <div>
          <p className="text-xs text-slate-500 dark:text-slate-400">{t('scenario.projectedCashFlow')}</p>
          <p className="text-lg font-primary font-bold text-emerald-600 dark:text-emerald-400 tabular-nums">
            {result.achieved_cash_flow >= 0 ? '+' : ''}${Math.round(result.achieved_cash_flow).toLocaleString()}/mo
          </p>
        </div>
        <Button
          data-testid="solver-apply-btn"
          onClick={onApply}
          className="bg-violet-600 hover:bg-violet-700 text-white"
        >
          {t('scenario.applyResult')}
        </Button>
      </div>
    </div>
  );
};

export default SolverResult;
